import { getOnLoadRegBranchPayloadFromStorage } from '../../utils/regBranchOnLoadPayload';
import { getManagingBranchNameForApi } from '../../utils/managingBranchName';

/** Default IANA timezone for POST /GetRestaurantOrders dispatch_sort_time. */
export const ACTIVE_ORDERS_DEFAULT_TIMEZONE = 'America/Chicago';

/** Resolves regionalBranch for GetActiveOrders (kitchen_managingBranchName). */
export function getRegionalBranchForOrdersApi() {
  const fromSession = getOnLoadRegBranchPayloadFromStorage()?.branchName;
  if (fromSession) return fromSession;
  return getManagingBranchNameForApi();
}

/** Formats a Date as MM/DD/YYYY for GetActiveOrders `date` body field. */
export function formatDateForActiveOrdersApi(d = new Date()) {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

/** Formats a Date as YYYY-MM-DD (tomorrow slot for GetNextDayPromoStoreOrders). */
export function formatDateISOForOrdersApi(d = new Date()) {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
}

/** Fixed [today MM/DD/YYYY, tomorrow YYYY-MM-DD] for POST /GetNextDayPromoStoreOrders. */
export function getNextDayPromoDefaultDates() {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return [formatDateForActiveOrdersApi(today), formatDateISOForOrdersApi(tomorrow)];
}

/**
 * Parses API geolocation objects into [lat, lng] for Leaflet.
 * Supports { latitude, longitude }, { lat, lng }, and GeoJSON Point { coordinates: [lng, lat] }.
 */
export function parseGeoLocationToLatLng(geo) {
  if (!geo || typeof geo !== 'object') return null;

  const lat = geo.latitude ?? geo.lat;
  const lng = geo.longitude ?? geo.lng ?? geo.lon;
  if (lat != null && lng != null) {
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
      if (latNum >= -90 && latNum <= 90 && lngNum >= -180 && lngNum <= 180) {
        return [latNum, lngNum];
      }
    }
  }

  const coords = geo.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    const lngNum = Number(coords[0]);
    const latNum = Number(coords[1]);
    if (
      Number.isFinite(latNum) &&
      Number.isFinite(lngNum) &&
      latNum >= -90 &&
      latNum <= 90 &&
      lngNum >= -180 &&
      lngNum <= 180
    ) {
      return [latNum, lngNum];
    }
  }

  return null;
}

/** Normalizes order delivery_date to YYYY-MM-DD for day-tab matching. */
export function normalizeOrderDeliveryDateKey(dateStr) {
  const s = String(dateStr ?? '').trim();
  if (!s) return null;

  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  }

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  return null;
}

/** ISO keys for today / tomorrow used to bucket orders into day tabs. */
export function getNextDayPromoDayKeys() {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return {
    today: formatDateISOForOrdersApi(today),
    tomorrow: formatDateISOForOrdersApi(tomorrow),
  };
}

export function orderMatchesNextDayPromoDay(order, day, dayKeys = getNextDayPromoDayKeys()) {
  const orderKey = normalizeOrderDeliveryDateKey(order?.delivery_date);
  if (!orderKey) return false;
  return day === 'today' ? orderKey === dayKeys.today : orderKey === dayKeys.tomorrow;
}

/**
 * Normalizes axios interceptor response from POST /GetRestaurantOrders.
 * @returns {{ ok: boolean, message?: string, orders: Array, count: number, pagination: object|null }}
 */
export function parseActiveOrdersResponse(res) {
  if (res?.hasError) {
    return {
      ok: false,
      message: res.message || 'Failed to load orders.',
      orders: [],
      count: 0,
      pagination: null,
    };
  }

  const payload = res?.data ?? {};
  if (payload.success !== true) {
    return {
      ok: false,
      message: payload.message || 'Could not load orders.',
      orders: [],
      count: 0,
      pagination: payload.pagination ?? null,
    };
  }

  return {
    ok: true,
    message: payload.message,
    orders: Array.isArray(payload.order) ? payload.order : [],
    count: typeof payload.count === 'number' ? payload.count : 0,
    pagination: payload.pagination ?? null,
  };
}
